/**
 * Draft mutations for path-attached items (landers + offers). Add/remove/move
 * are deduped by id so attaching the same lander twice is a no-op.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  BinomDraftItem,
  BinomDraftPath,
  BinomFlowDraft,
} from './binom-flow-draft.types';

export interface PathLocator {
  groupIdx: number | null;
  pathIdx: number;
}

export function getPath(
  d: BinomFlowDraft,
  loc: PathLocator,
): BinomDraftPath | undefined {
  if (loc.groupIdx === null) return d.defaultPaths[loc.pathIdx];
  return d.pathGroups[loc.groupIdx]?.paths[loc.pathIdx];
}

export function addLander(
  d: BinomFlowDraft,
  loc: PathLocator,
  item: BinomDraftItem,
): void {
  const p = getPath(d, loc);
  if (!p) return;
  if (p.landers.some((l) => l.id === item.id)) return;
  p.landers.push(item);
}

export function removeLander(
  d: BinomFlowDraft,
  loc: PathLocator,
  idx: number,
): void {
  const p = getPath(d, loc);
  if (!p || idx < 0 || idx >= p.landers.length) return;
  p.landers.splice(idx, 1);
}

export function moveLander(
  d: BinomFlowDraft,
  loc: PathLocator,
  from: number,
  to: number,
): void {
  const p = getPath(d, loc);
  if (!p) return;
  moveInPlace(p.landers, from, to);
}

export function addOffer(
  d: BinomFlowDraft,
  loc: PathLocator,
  item: BinomDraftItem,
): void {
  const p = getPath(d, loc);
  if (!p) return;
  if (p.offers.some((o) => o.id === item.id)) return;
  p.offers.push(item);
}

export function removeOffer(
  d: BinomFlowDraft,
  loc: PathLocator,
  idx: number,
): void {
  const p = getPath(d, loc);
  if (!p || idx < 0 || idx >= p.offers.length) return;
  p.offers.splice(idx, 1);
}

export function moveOffer(
  d: BinomFlowDraft,
  loc: PathLocator,
  from: number,
  to: number,
): void {
  const p = getPath(d, loc);
  if (!p) return;
  moveInPlace(p.offers, from, to);
}

function moveInPlace<T>(arr: T[], from: number, to: number): void {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= arr.length ||
    to >= arr.length
  )
    return;
  const [item] = arr.splice(from, 1);
  arr.splice(to, 0, item);
}
